<template>
  <div class="overflow-hidden">
    <div class="bg-stone-75">
      <GiftcardBanner
        :hide-button="!bannerBlock?.callToAction?.label"
        :text="bannerBlock?.mainMediaBlock?.title || t('giftcard.header.title')"
        :action="openDateRequestFromGiftcardBanner"
        :description="bannerBlock?.mainMediaBlock?.subtitle"
        :action-label="bannerBlock?.callToAction?.label"
        :featured-media="bannerFeaturedMedia"
        test-id="giftcard-page-title"
      />
    </div>

    <GiftcardThemesShowcase
      class="w-screen md:max-w-(--breakpoint-xl) mx-auto px-4"
    />

    <div v-for="blockData in otherBlocks" :key="blockData?.id">
      <GenericBlock
        :component-type="blockData.__typename"
        :content="blockData"
        :block-props="showcaseBlockProps(blockData.__typename)"
      />
    </div>
  </div>
</template>
<script setup>
import GiftcardThemesShowcase from '~/components/giftcard/GiftcardThemesShowcase.vue'
import { useStrapiPageSeo } from '~/composables/useStrapiPage'

const { toggleDateRequestSidebar } = useUiState()
const route = useRoute()
const openDateRequestFromGiftcardBanner = () => {
  toggleDateRequestSidebar(route.name?.toString(), 'strapi_component')
}
const { t, locale } = useI18n()

const pageData = await useStrapiPage({
  slug: `giftcard_${locale.value}`,
  locale: convertLocaleIntoBCP47(locale.value),
})
const blocksData = computed(() => pageData?.data?.value?.pages?.[0]?.contents)

const bannerBlock = computed(() =>
  blocksData.value?.find((b) => b.__typename === 'ComponentSharedBanner')
)
const otherBlocks = computed(
  () => blocksData.value?.filter((b) => b !== bannerBlock.value) ?? []
)
// Both event-list blocks on this page stay at two grid rows (8 products on
// desktop) so the editorial blocks below them are not pushed out of reach, and
// their "show more" sits beside the section title rather than as a button
// below the grid.
const showcaseBlockProps = (componentType) =>
  componentType === 'ComponentSharedEventsList'
    ? { maxRows: 2, showMoreInTitle: true }
    : undefined

const bannerFeaturedMedia = computed(() => {
  const url = bannerBlock.value?.mainMediaBlock?.media?.url
  return url ? [{ src: url, alt: '' }] : []
})

const giftcardAmounts = [
  parseInt(t('giftcard.option_1')),
  parseInt(t('giftcard.option_2')),
  parseInt(t('giftcard.option_3')),
  parseInt(t('giftcard.option_4')),
]
const bannerImage = bannerBlock.value?.mainMediaBlock?.media?.url

// The cart adds this per-country delivery cost to every giftcard card or box,
// so the schema's shipping rates include it too.
const { data: deliveryCostsResponse } = await useKftApiFetch(
  'v1/store/shipping/costs?include=country'
)
const countryDeliveryCosts = Object.fromEntries(
  (deliveryCostsResponse.value?.data ?? [])
    .filter((cost) => cost.country?.data?.codeIsoAlpha2)
    .map((cost) => [cost.country.data.codeIsoAlpha2, Number(cost.value.amount)])
)

const { script: schemas } = useGiftcardsSchema(
  giftcardAmounts,
  bannerImage,
  countryDeliveryCosts
)
definePageMeta({
  layout: 'default-max-screen',
  contentGroup: 'giftcard',
  preloaderType: 'strapi',
})

useStrapiPageSeo({
  pageData,
  schemas,
  // Used only when the CMS has no meta image; the banner photo beats the logo.
  fallbackImage: bannerImage,
})
</script>
